import { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { cn } from '@utils/cn';
import { CALENDLY_URL } from '@constants';

const navItems = [
  { label: 'About Us', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Packages', href: '/packages' },
  { label: 'How We Work', href: '/how-we-work' },
  { label: 'News', href: '/news' },
];

export function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      setIsScrolled(scrollY > 20);

      // Calculate scroll progress for the progress bar
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollY / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (href: string) => {
    setMobileOpen(false);
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <nav
        className={cn(
          'fixed top-0 left-0 right-0 z-50 border-b border-transparent transition-all duration-300',
          isScrolled ? 'bg-primary/90 backdrop-blur-xl border-white/10' : 'bg-primary'
        )}
      >
        {/* Scroll progress bar */}
        {scrollProgress > 1 && (
          <div
            className="absolute bottom-0 left-0 h-0.5 bg-teal"
            style={{ width: `${scrollProgress}%` }}
          />
        )}

        <div className="container mx-auto flex items-center justify-between h-16 px-4 lg:px-8">
          <a href="/" className="flex items-center gap-2 group">
            <img
              src="/images/logo.svg"
              alt="Fractional CISO Services"
              width="36"
              height="36"
              className="h-9 w-auto transition-transform duration-300 group-hover:scale-110"
            />
            <span className="font-heading text-xl font-bold text-primary-foreground tracking-tight">
              The CISO Network
            </span>
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map(item => (
              <a
                key={item.label}
                href={item.href}
                onClick={e => {
                  if (item.href.startsWith('/#')) {
                    e.preventDefault();
                    scrollToSection(item.href.replace('/', ''));
                  }
                }}
                className="relative text-sm font-medium text-primary-foreground/70 hover:text-teal transition-colors group"
              >
                {item.label}
                <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-teal transition-all duration-300 group-hover:w-full" />
              </a>
            ))}
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-hero text-sm px-6 py-2"
            >
              Book a Call
            </a>
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden text-primary-foreground p-2 hover:bg-white/5 rounded-lg transition-colors"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        <div
          className={cn(
            'md:hidden absolute top-full left-0 right-0 bg-primary border-b border-white/10 overflow-hidden transition-all duration-300',
            mobileOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
          )}
        >
          <div className="px-4 py-4 space-y-1">
            {navItems.map(item => (
              <a
                key={item.label}
                href={item.href}
                onClick={e => {
                  if (item.href.startsWith('/#')) {
                    e.preventDefault();
                    scrollToSection(item.href.replace('/', ''));
                  }
                  setMobileOpen(false);
                }}
                className="block py-3 px-4 text-sm font-medium text-primary-foreground/70 hover:text-teal hover:bg-white/5 rounded-lg transition-all"
              >
                {item.label}
              </a>
            ))}
            <div className="pt-2">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-hero w-full text-sm"
              >
                Book a Call
              </a>
            </div>
          </div>
        </div>
      </nav>

      {/* Overlay for mobile menu */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}
    </>
  );
}
